<script setup lang="ts">
import { $t } from '@repo/locales';
import { Button, Loading, showToast, showConfirmDialog } from 'vant';
import { QrScan } from '@/components';
import { getAppConfig, getFabricShareInfo, generateFabricInfo } from '@/utils';
import { useParamStore } from '@/store/param.ts';
import { useRoute, useRouter } from 'vue-router';
import axios from 'axios';
import { joinUrl } from '@repo/utils';
import { type FavoriteStore, getStyle3dFabricApi, saveFavorite } from '@/api';
import { nextTick, ref } from 'vue';
import {
  parseStyle3dUShortUrlV3,
  oldCloudKnitReg,
  cloudKnitReg,
  cloudKnitFabricReg,
  style3dReg,
  style3dUShortRegV3,
  style3dRShortRegV3
} from '@/utils';
import { useRelationStore, useSelectionStore, useStyle3dStore, storeToRefs } from '@repo/stores';
import { assignNewMaterialIndex } from '@/utils/materialOverride';
import { type FabricShareInfo, useStyle3d } from '@repo/style3d';
import { useFabricStore } from '@/store/fabric.ts';
import type { AxiosResponse } from 'axios';


const validator = (text: string) => {
  return oldCloudKnitReg.test(text) ||
    cloudKnitReg.test(text) ||
    cloudKnitFabricReg.test(text) ||
    style3dReg.test(text) ||
    style3dUShortRegV3.test(text) ||
    style3dRShortRegV3.test(text);
};

const { URL_PARSE_URL, OSS_BASE_URL, INDIVIDUAL_FABRIC_SHARES } = getAppConfig();
const fabricRootPath = `${OSS_BASE_URL}${INDIVIDUAL_FABRIC_SHARES}`;

const { refreshLoadResourceByRoute } = useParamStore();
const paramStore = useParamStore();

const router = useRouter();
const route = useRoute();
const loading = ref(false);

const selectionStore = useSelectionStore();
const relationStore = useRelationStore();
const fabricStore = useFabricStore();
const style3dStore = useStyle3dStore();
const { resolution } = storeToRefs(style3dStore);
const { loadFabric } = useStyle3d();

let originUrl: string = '';

// ─── 核心：带"是否同步替换"确认的面料替换逻辑 ───────────────────────────────

const doReplaceFabric = async (
  fabricId: string,
  resourceType: 'cloudknit' | 'style3d',
  itemOriginUrl: string,
  targetShapes: any[],
  materialIndex: number,
  fabricShareInfo: Omit<FabricShareInfo, 'mask' | 'metaData' | 'doc' | 'enterprise_id'>
) => {
  const updatedList = targetShapes.map(shape => {
    const fabricInfoType = shape.type ? shape.type : 'main';
    return {
      ...shape,
      ...fabricShareInfo.info[fabricInfoType] ?? fabricShareInfo.info['main'],
      color: ''
    };
  });

  const fabric = { id: fabricId, type: resourceType, originUrl: itemOriginUrl };

  if (resourceType === 'style3d') {
    const shapes = await loadFabric(updatedList, { fabricRootPath: '', resolution: resolution.value, fabric });
    relationStore.updateShape(shapes);
  } else {
    const fabricRoot = joinUrl(fabricRootPath, fabricId);
    const shapes = await loadFabric(updatedList, { fabricRootPath: fabricRoot, resolution: resolution.value, fabric });
    relationStore.updateShape(shapes);
  }

  try {
    let fullFabricInfo: any;
    if (resourceType === 'style3d') {
      const style3dFabric = await getStyle3dFabricApi(fabricId);
      const { xhr_path, thumb_path, width, height } = style3dFabric.data;
      const keyMap = style3dFabric.create_info.fields.map((f: any) => ({ name: f.name, key: f.key }));
      const fabricProperty = keyMap
        .map((f: any) => ({ name: f.name, key: f.key, value: style3dFabric.data[f.key] }))
        .filter((f: any) => f.value);
      fullFabricInfo = {
        id: `style3d-${fabricId}`, originUrl: itemOriginUrl, fabricId,
        info: { name: xhr_path, width, height },
        resourceType: 'style3d' as const,
        enterpriseId: '', enterpriseName: '', thumb: thumb_path, xhr_path,
        properties: fabricProperty, mask: { srcImage: undefined, colors: undefined, list: [] }
      };
    } else {
      const fabricRoot = joinUrl(fabricRootPath, fabricId);
      const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
      const info = infoResult.data;
      const thumbUrl = info.info?.thumbnail
        ? joinUrl(fabricRoot, 'fabric', info.info.thumbnail.name)
        : info.info.main ? joinUrl(fabricRoot, 'fabric', info.info.main.name) : '';
      fullFabricInfo = {
        id: fabricId, originUrl: itemOriginUrl, fabricId,
        info: info.metaData.info, thumb: thumbUrl, xhr_path: undefined,
        enterpriseId: info.metaData.enterpriseID || '',
        enterpriseName: info.metaData.enterpriseName || '',
        resourceType: 'cloudknit' as const,
        properties: info.metaData.property || [],
        styleProperties: info.metaData?.propertyStyle,
        carousels: info.doc?.map((d: any) => ({ ...d, url: joinUrl(fabricRoot, d.name) })) || [],
        logo: info.metaData.logo,
        mask: { srcImage: info.mask?.srcImage, colors: info.mask?.colors, list: info.mask?.list || [] }
      };
    }
    await fabricStore.setFabricInfo(fullFabricInfo, materialIndex);
    const currentSelectedIndex = fabricStore.getSelectedRegionIndex(materialIndex);
    const newRegionCount = fullFabricInfo.mask?.colors?.length || 0;
    if (newRegionCount > 0 && currentSelectedIndex >= newRegionCount) {
      fabricStore.setSelectedRegionIndex(0, materialIndex);
    }
  } catch (e) {
    console.error('[Scan] 更新fabricStore失败:', e);
  }
};

/**
 * 当有版片选中时，询问是否同步替换，并执行替换 + 路由更新。
 * 返回 true 表示已处理（不需要走原有的 refreshLoadResourceByRoute 流程）。
 */
const handleFabricReplaceWithConfirm = async (
  fabricId: string,
  resourceType: 'cloudknit' | 'style3d',
  itemOriginUrl: string
): Promise<boolean> => {
  const { selectedFabricId } = selectionStore;
  if (!selectedFabricId) return false;

  let fabricShareInfo: Omit<FabricShareInfo, 'mask' | 'metaData' | 'doc' | 'enterprise_id'>;
  try {
    if (resourceType === 'style3d') {
      const style3dFabric = await getStyle3dFabricApi(fabricId);
      const { xhr_path, thumb_path, width, height } = style3dFabric.data;
      const generateRibInfo = paramStore.resourceParam.scoResource.type !== 'style3d';
      fabricShareInfo = await generateFabricInfo(
        { info: { width, height }, thumb: thumb_path, xhr_path },
        generateRibInfo
      );
    } else {
      const fabricRoot = joinUrl(fabricRootPath, fabricId);
      const infoResult: AxiosResponse<FabricShareInfo> = await axios.get(joinUrl(fabricRoot, 'info.json'));
      fabricShareInfo = infoResult.data;
    }
  } catch (e) {
    console.error('[Scan] 获取面料信息失败:', e);
    return false;
  }

  const found = relationStore.shapeList.find(s => s.id === selectedFabricId);
  const materialIndex = found?.materialIndex ?? 0;
  const sameMaterialShapes = relationStore.shapeList.filter(s => (s.materialIndex ?? 0) === materialIndex);
  const otherShapes = sameMaterialShapes.filter(s => s.id !== selectedFabricId);

  let syncMode: 'all' | 'current' = 'all';
  if (otherShapes.length > 0) {
    try {
      await showConfirmDialog({
        title: $t('sync.title'),
        message: $t('sync.message', { count: otherShapes.length }),
        confirmButtonText: $t('sync.confirm'),
        cancelButtonText: $t('sync.cancel'),
      });
      syncMode = 'all';
    } catch {
      syncMode = 'current';
    }
  }

  const { query, params } = route;
  const currentParams = params as any;
  const newParams = { ...currentParams };
  const newQuery: any = { ...query, originUrl: btoa(itemOriginUrl), color: undefined };

  if (query.colors) {
    try {
      const colorsObj = JSON.parse(decodeURIComponent(query.colors as string));
      delete colorsObj[materialIndex];
      newQuery.colors = Object.keys(colorsObj).length > 0
        ? encodeURIComponent(JSON.stringify(colorsObj))
        : undefined;
    } catch {
      newQuery.colors = undefined;
    }
  }

  if (syncMode === 'all') {
    await doReplaceFabric(fabricId, resourceType, itemOriginUrl, sameMaterialShapes, materialIndex, fabricShareInfo);
    if (materialIndex === 0) { newParams.id = fabricId; newParams.type = resourceType; }
    else if (materialIndex === 1) { newParams.id1 = fabricId; }
    else if (materialIndex === 2) { newParams.id2 = fabricId; }
    await router.replace({ params: newParams, query: newQuery });
  } else {
    const piece = found?.piece ?? '';
    const newIndex = await assignNewMaterialIndex(piece, materialIndex, relationStore.shapeList, router, fabricId);

    if (newIndex === null) {
      await doReplaceFabric(fabricId, resourceType, itemOriginUrl, [found!].filter(Boolean), materialIndex, fabricShareInfo);
      await router.replace({ params: newParams, query: newQuery });
      return true;
    }

    if (found) {
      relationStore.updateShape([{ ...found, materialIndex: newIndex }]);
    }
    const currentShape = relationStore.shapeList.find(s => s.id === selectedFabricId);
    if (currentShape) {
      await doReplaceFabric(fabricId, resourceType, itemOriginUrl, [currentShape], newIndex, fabricShareInfo);
    }

    const latestQuery = { ...router.currentRoute.value.query } as any;
    if (latestQuery.colors) {
      try {
        const colorsObj = JSON.parse(decodeURIComponent(latestQuery.colors));
        delete colorsObj[newIndex];
        latestQuery.colors = Object.keys(colorsObj).length > 0
          ? encodeURIComponent(JSON.stringify(colorsObj))
          : undefined;
      } catch {
        latestQuery.colors = undefined;
      }
    }
    latestQuery.originUrl = btoa(itemOriginUrl);
    latestQuery.color = undefined;
    await router.replace({ params: router.currentRoute.value.params, query: latestQuery });
  }

  return true;
};

const onScanSuccess = async (val: string) => {
  originUrl = val;
  try {
    loading.value = true;
    switch (route.name) {
      case 'favorite':
        await handleFavoriteCase(val);
        break;
      default:
        await handleNormalCase(val);
        break;
    }
  } catch (e: any) {
    loading.value = false;
    showToast(e.message);
  }
};

const handleNormalCase = async (val: string) => {
  if (cloudKnitReg.test(val) || oldCloudKnitReg.test(val)) {
    const match = val.match(cloudKnitReg) || val.match(oldCloudKnitReg);
    if (match && match[1]) {
      await handleCloudKnitResource(match[1]);
    }
  } else if (cloudKnitFabricReg.test(val)) {
    const match = val.match(cloudKnitFabricReg);
    if (match && match[1]) {
      await handleCloudKnitFabricResource(match[1]);
    }
  } else if (style3dReg.test(val)) {
    await handleStyle3dFabricResource(val);
  } else if (style3dUShortRegV3.test(val)) {
    const url = await parseStyle3dUShortUrlV3(val);
    await handleStyle3dFabricResourceV3(url);
  } else if (style3dRShortRegV3.test(val)) {
    const url = await parseStyle3dUShortUrlV3(val);
    await handleStyle3dResourceV3(url);
  }
};

const handleFavoriteCase = async (val: string) => {
  if (cloudKnitFabricReg.test(val)) {
    const match = val.match(cloudKnitFabricReg);
    if (match && match[1]) {
      const info = await getFabricShareInfo(match[1], originUrl);
      await favoriteFabric(info);
    }
  } else if (style3dReg.test(val)) {
    await favoriteStyle3dFabric(val);
  } else if (style3dUShortRegV3.test(val)) {
    const url = await parseStyle3dUShortUrlV3(val);
    await favoriteStyle3dFabric(url);
  } else {
    showToast('请到3D或详情页扫码');
  }
};

const handleCloudKnitResource = async (id: string) => {
  await router.replace({
    name: 'fabric',
    params: { ...route.params },
    query: {
      ...route.query,
      scoId: id,
      originScoUrl: btoa(originUrl)
    }
  });
  await refreshLoadResourceByRoute();
};

const handleCloudKnitFabricResource = async (id: string) => {
  const handled = await handleFabricReplaceWithConfirm(id, 'cloudknit', originUrl);
  if (handled) return;

  await router.replace({
    params: { id, type: 'cloudknit' },
    query: { ...route.query, originUrl: btoa(originUrl), color: undefined }
  });
  await refreshLoadResourceByRoute();
};

const handleStyle3dFabricResource = async (url: string) => {
  const regex = /fabric\/(\d+)(?:\?|\b)/;
  let id = url.match(regex)?.[1];
  try {
    const response = await axios.get<string>(joinUrl(URL_PARSE_URL, `/style3d/initial_data?url=${encodeURIComponent(url)}`));
    id = (response.data as any).m3dStore.detailData.extra?.renderData?.id;
  } catch (e: any) {
    console.log(e.message);
  }
  if (!id) {
    throw new Error('解析错误');
  }

  const handled = await handleFabricReplaceWithConfirm(id, 'style3d', originUrl);
  if (handled) return;

  await router.replace({ params: { id, type: 'style3d' }, query: { ...route.query, originUrl: btoa(originUrl) } });
  await refreshLoadResourceByRoute();
};

const handleStyle3dFabricResourceV3 = async (url: string) => {
  const regex = /fabric\/(\d+)(?:\?|\b)/;
  const id = url.match(regex)?.[1];
  if (!id) {
    throw new Error('解析错误');
  }

  const handled = await handleFabricReplaceWithConfirm(id, 'style3d', originUrl);
  if (handled) return;

  await router.replace({ params: { id, type: 'style3d' }, query: { ...route.query, originUrl: btoa(originUrl) } });
  await refreshLoadResourceByRoute();
};

const handleStyle3dResourceV3 = async (url: string) => {
  try {
    const regex = /(style|product)\/(\d+)(?:.*?[?&]share_state_id=(\d+))?(?:.*?[?&]scope_version=(\d+))?/;
    const match = url.match(regex);
    if (!match) {
      throw new Error('解析错误');
    }

    const type = match[1] as string;
    const resource_id = match[2] as string;
    const share_state_id = match[3];
    const scope_version = match[4];

    const queryParams = new URLSearchParams({});
    if (share_state_id) {
      queryParams.append('share_state_id', share_state_id);
    }
    if (scope_version) {
      queryParams.append('scope_version', scope_version);
    }

    const response = await axios.get<string>(joinUrl(URL_PARSE_URL, `/api_v3/style3d/resource/${type}/${resource_id}?${queryParams.toString()}`));

    const sco = (response.data as any).data.model_path.cloth_src.src;
    if (!sco) {
      throw new Error('解析错误');
    }
    await router.replace({
      name: 'fabric',
      params: { ...route.params },
      query: { sco: btoa(sco), scoId: resource_id, originScoUrl: btoa(originUrl) }
    });
    await refreshLoadResourceByRoute();
  } catch (error) {
    console.error('Error fetching the data:', error);
  }
};

const favoriteFabric = async (fabric: Omit<FavoriteStore, 'date' | 'terms' | 'resourceType'>) => {
  const data = { ...fabric, fabricId: fabric.id, resourceType: 'cloudknit' as const, memo: '', num: 1 };
  await saveFavorite(data);
  router.go(0);

  await nextTick(() => {
    showToast($t('message.favorite-success'));
  });
};

const favoriteStyle3dFabric = async (url: string) => {
  const response = await axios.get<string>(joinUrl(URL_PARSE_URL, `/style3d/initial_data?url=${encodeURIComponent(url)}`));
  const id = (response.data as any).m3dStore.detailData.extra?.renderData?.id;
  if (!id) {
    throw new Error('解析错误');
  }

  const style3dFabric = await getStyle3dFabricApi(id);

  const keyMap = style3dFabric.create_info.fields.map(item => ({ name: item.name, key: item.key }));
  const fabricProperty = keyMap
    .map((item) => ({ name: item.name, key: item.key, value: style3dFabric.data[item.key] }))
    .filter(item => item.value);

  await saveFavorite({
    id: `style3d-${id}`,
    originUrl,
    fabricId: `${id}`,
    thumb: style3dFabric.data.thumb_path,
    resourceType: 'style3d',
    properties: fabricProperty,
    info: {
      name: style3dFabric.data.name
    }
  });

  router.go(0);
  await nextTick(() => {
    showToast($t('message.favorite-success'));
  });
};
</script>

<template>
  <QrScan :validator="validator" :callback="onScanSuccess">
    <template #scan-icon="slotProps">
      <Button type="primary">
        {{ $t('tabbar.scan') }}
        <template #loading>
          <Loading v-if="slotProps.loading" size="18" />
        </template>
      </Button>
    </template>
  </QrScan>

</template>

<style scoped>

</style>
